import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

// Keep translations colocated so the widget can bootstrap i18n without async fetches.
const resources = {
  de: {
    widget: {
      // Confirmation Dialog
      confirmation: {
        cancel: 'Abbrechen',
        confirm: 'Bestätigen',
        updateRequest: 'Anfrage aktualisieren',
        updateDescription: 'Hotels dieser Buchung werden mit aktualisierten Informationen neu angefragt',
      },
      // Capsule tabs
      tabs: {
        place: 'Ort',
        date: 'Termin',
        participants: 'Teilnehmer',
        rooms: 'Räume',
        overnight: 'Übernachtungen',
        catering: 'Verpflegung',
        tooltip: {
          rooms: 'Räume',
        },
      },
      // Form placeholders
      placeholder: {
        placeOrHotel: 'Ort oder Hotel eingeben',
        noDate: 'keine',
        notSet: 'nicht gesetzt',
      },
      // Search results
      search: {
        searching: 'Suche...',
        noResults: 'Keine Ergebnisse gefunden',
        hotels: 'Hotels',
        places: 'Orte',
        showInactiveHotels: 'Inaktive Hotels anzeigen',
      },
      // Buttons
      buttons: {
        search: 'Suchen',
        details: 'Details',
        close: 'X',
        addRoom: '+ Raum hinzufügen',
        continue: 'Weiter',
        planMeeting: 'Seminar planen',
      },
      // Service types
      serviceTypes: {
        fullDay: 'Ganztags',
        halfDay: 'Halbtags',
        small: 'Klein',
        regular: 'Ganztags',
        medium: 'Halbtags',
        packageSmall: 'Pauschale "klein"',
        packageHalfDay: 'Pauschale "Halbtags"',
        packageFullDay: 'Pauschale "Ganztags"',
        packageNone: 'Keine Standard Pauschale',
        packageNoneDescription: 'Cateringwünsche beim Checkout angeben.',
        coffeeBreak: 'Kaffeepause',
        lunch: 'Mittagessen',
        roomAndTech: 'In allen Pauschalen ist die Raumnutzung sowie die Standardtechnik enthalten.',
        dinner: 'Abendessen',
        dinnerTime: '18:00 bis 21:00 Uhr möglich',
        added: 'hinzugefügt',
        enterPersonCount: 'Bitte tragen Sie die Anzahl der Personen für',
        in: 'ein.',
        personCount: 'Anzahl Personen',
        personsAssigned: 'Personen von',
        assigned: 'zugeteilt',
        assignedToStandardPackages: '{{assigned}} von {{total}} Teilnehmenden mit Standardpauschale',
        participantsWithoutStandardPackage: '{{count}} Teilnehmende ohne Standardpauschale',
      },
      // Addons
      addons: {
        coffee: 'Empfangskaffee',
        dinner: 'Abendessen',
        inconsistent_service_warning: 'Die Zusatzleistung ist derzeit nur für diesen Tag ausgewählt.',
        inconsistent_service_warning_with_count: 'Die Zusatzleistung ist derzeit für {{selectedDays}} von {{totalDays}} Tagen ausgewählt.',
      },
      // Table headers
      table: {
        duration: 'Dauer',
        seating: 'Bestuhlung',
        seatCount: 'Anzahl Plätze',
        room: 'Raum',
      },
      // Time options
      time: {
        fullDay: 'Ganztägig',
        morning: 'Vormittag',
        afternoon: 'Nachmittag',
      },
      // Seating types
      seating: {
        theater: 'Theater',
        parliament: 'Parlament',
        circle: 'Kreis',
        banquet: 'Bankett',
        cocktail: 'Cocktail',
        block: 'Block',
        uform: 'U-Form',
      },
      // Room descriptions
      room: {
        extensionNight: 'Verlängerungsnacht',
        day: 'Tag',
        arrivalPrevDay: 'Anreise Vortag',
        peopleOf: 'Personen von',
        assigned: 'zugeteilt',
        personsInSingle: 'Personen im Einzelzimmer',
        personsInDouble: 'Personen im Doppelzimmer',
      },
      // Day switch
      daySwitch: {
        arrivalPrevDay: 'Anreise Vortag',
        day: 'Tag',
      },
      // Headers
      headers: {
        pleaseEnterParticipants: 'Bitte geben Sie die Teilnehmeranzahl an.',
        pleaseEnterRoomDetails: 'Bitte geben Sie die Seminarraumdetails an.',
        pleaseEnterOvernights: 'Bitte geben Sie die Anzahl der Übernachtungen an.',
        pleaseEnterCatering: 'Bitte wählen Sie Ihre Verpflegungspauschale.',
      },
      // Labels
      labels: {
        seating: 'Bestuhlung',
        participants: 'Teilnehmer',
        uform: 'U-Form',
      },
      // Time labels
      timeLabels: {
        from: 'Von',
        to: 'Bis',
      },
      // Room labels
      roomLabels: {
        seminarDays: 'Seminartage',
        additionalNight: 'Zusatznacht',
        on: 'auf',
      },
      // Room equipment
      roomEquipment: {
        included: 'inkludiert:',
        beamer: 'Beamer & Leinwand / Flatscreen',
        flipchart: 'Flipchart',
        pinboard: 'Pinnwand',
      },
      roomRequirements: {
        title: 'Weitere Raumanforderungen',
        airConditioning: 'Klimaanlage',
        daylight: 'Tageslicht',
        minSize: 'Mindestraumgröße',
        noMinimum: 'Keine Mindestgröße',
      },
    },
  },
  en: {
    widget: {
      // Confirmation Dialog
      confirmation: {
        cancel: 'Cancel',
        confirm: 'Confirm',
        updateRequest: 'Update Request',
        updateDescription: 'Hotels in this booking will be re-requested with updated information',
      },
      // Capsule tabs
      tabs: {
        place: 'Location',
        date: 'Date',
        participants: 'Participants',
        rooms: 'Rooms',
        overnight: 'Accommodation',
        catering: 'Catering',
        tooltip: {
          rooms: 'Rooms',
        },
      },
      // Form placeholders
      placeholder: {
        placeOrHotel: 'Enter location or hotel',
        noDate: 'none',
        notSet: 'not set',
      },
      // Search results
      search: {
        searching: 'Searching...',
        noResults: 'No results found',
        hotels: 'Hotels',
        places: 'Places',
        showInactiveHotels: 'show inactive hotels',
      },
      // Buttons
      buttons: {
        search: 'Search',
        details: 'Details',
        close: 'X',
        addRoom: '+ Add Room',
        continue: 'Continue',
        planMeeting: 'Plan Meeting',
      },
      // Service types
      serviceTypes: {
        fullDay: 'Full Day',
        halfDay: 'Half Day',
        small: 'Small',
        regular: 'Full Day',
        medium: 'Half Day',
        packageSmall: 'Small Package',
        packageHalfDay: 'Half Day Package',
        packageFullDay: 'Full Day Package',
        packageNone: 'No Standard Package',
        packageNoneDescription: 'Add catering requests at checkout',
        coffeeBreak: 'Coffee Break',
        lunch: 'Lunch',
        roomAndTech: 'All packages include room usage and standard technology.',
        dinner: 'Dinner',
        dinnerTime: 'Available 6:00 PM to 9:00 PM',
        added: 'added',
        enterPersonCount: 'Please enter the number of people for',
        in: '',
        personCount: 'Number of People',
        personsAssigned: 'people of',
        assigned: 'assigned',
        assignedToStandardPackages: '{{assigned}} of {{total}} participants assigned to standard packages',
        participantsWithoutStandardPackage: '{{count}} participants without a standard package',
      },
      // Addons
      addons: {
        coffee: 'Welcome Coffee',
        dinner: 'Dinner',
        inconsistent_service_warning: 'The additional service is currently only selected for this day.',
        inconsistent_service_warning_with_count: 'The additional service is currently selected for {{selectedDays}} out of {{totalDays}} days.',
      },
      // Table headers
      table: {
        duration: 'Duration',
        seating: 'Seating',
        seatCount: 'Number of Seats',
        room: 'Room',
      },
      // Time options
      time: {
        fullDay: 'All Day',
        morning: 'Morning',
        afternoon: 'Afternoon',
      },
      // Seating types
      seating: {
        theater: 'Theater',
        parliament: 'Parliament',
        circle: 'Circle',
        banquet: 'Banquet',
        cocktail: 'Cocktail',
        block: 'Block',
        uform: 'U-Shape',
      },
      // Room descriptions
      room: {
        extensionNight: 'Extension Night',
        day: 'Day',
        arrivalPrevDay: 'Arrival Previous Day',
        peopleOf: 'people of',
        assigned: 'assigned',
        personsInSingle: 'People in Single Room',
        personsInDouble: 'People in Double Room',
      },
      // Day switch
      daySwitch: {
        arrivalPrevDay: 'Arrival Previous Day',
        day: 'Day',
      },
      // Headers
      headers: {
        pleaseEnterParticipants: 'Please enter the number of participants.',
        pleaseEnterRoomDetails: 'Please enter the meeting room details.',
        pleaseEnterOvernights: 'Please enter the number of overnight stays.',
        pleaseEnterCatering: 'Please select your catering package.',
      },
      // Labels
      labels: {
        seating: 'Seating',
        participants: 'Participants',
        uform: 'U-Shape',
      },
      // Time labels
      timeLabels: {
        from: 'From',
        to: 'To',
      },
      // Room labels
      roomLabels: {
        seminarDays: 'Seminar Days',
        additionalNight: 'Additional Night',
        on: 'on',
      },
      // Room equipment
      roomEquipment: {
        included: 'included:',
        beamer: 'Projector & Screen / Flatscreen',
        flipchart: 'Flipchart',
        pinboard: 'Pinboard',
      },
      roomRequirements: {
        title: 'Additional room requirements',
        airConditioning: 'Air conditioning',
        daylight: 'Daylight',
        minSize: 'Minimum room size',
        noMinimum: 'No minimum size',
      },
    },
  },
};

// Initialize synchronously because widget consumers expect immediate translated labels.
if (!i18n.isInitialized) {
  i18n.use(initReactI18next).init({
    resources,
    lng: 'de', // Default language
    fallbackLng: 'de',
    defaultNS: 'widget',
    ns: ['widget'],
    interpolation: {
      escapeValue: false,
    },
    debug: false,
    keySeparator: '.',
    nsSeparator: ':',
    react: {
      useSuspense: false,
    },
    initImmediate: false, // Initialize synchronously
  });
}

export default i18n;
